/**
 * Beloved — private backup files.
 *
 * Backups contain only the person's saved prayers, saved verses, remembered
 * intentions, reading-size choice, and reminder preferences. Purchases, free
 * allowance counters, developer flags, and API configuration are deliberately
 * excluded. Import merges content rather than deleting what is already here.
 */

import { STORAGE_KEY as INTENTIONS_KEY, MAX_INTENTIONS } from './intentions';
import {
  REMINDER_TIMES_KEY,
  getReminderTimes,
  normaliseReminderTimes,
} from './notifications';

export const BACKUP_FORMAT = 'beloved-private-backup';
export const BACKUP_VERSION = 1;
export const SAVED_PRAYERS_KEY = 'beloved_saved_prayers';
export const SAVED_VERSES_KEY = 'beloved_saved_verses';
export const TEXT_SIZE_KEY = 'beloved_text_size';

const TEXT_SIZES = new Set(['comfortable', 'large', 'extraLarge']);

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

function readString(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeJson(key, value) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

function cleanPrayers(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item.id === 'string' && typeof item.body === 'string')
    .map((item) => ({
      id: item.id,
      title: typeof item.title === 'string' ? item.title : 'Saved prayer',
      meta: typeof item.meta === 'string' ? item.meta : '',
      body: item.body,
      personal:
        item.personal === true ||
        item.id.toLowerCase().startsWith('help-') ||
        item.id.toLowerCase().startsWith('personal-') ||
        (typeof item.meta === 'string' && item.meta.toLowerCase().includes('personal prayer')),
    }))
    .slice(0, 250);
}

function cleanVerses(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item.id === 'string' && typeof item.text === 'string')
    .map((item) => ({
      id: item.id,
      ref: typeof item.ref === 'string' ? item.ref : 'Saved verse',
      text: item.text,
      reflection: typeof item.reflection === 'string' ? item.reflection : '',
    }))
    .slice(0, 250);
}

function cleanIntentions(value) {
  if (!Array.isArray(value)) return [];
  const now = Date.now();
  return value
    .filter((item) => item && typeof item.theme === 'string' && item.theme)
    .map((item, index) => ({
      id: typeof item.id === 'string' && item.id ? item.id : `imported-${now}-${index}`,
      theme: item.theme,
      words: typeof item.words === 'string' ? item.words : '',
      label: typeof item.label === 'string' && item.label ? item.label : item.theme,
      createdAt: Number.isFinite(item.createdAt) ? item.createdAt : now,
      lastPrayedAt: Number.isFinite(item.lastPrayedAt) ? item.lastPrayedAt : now,
    }))
    .sort((a, b) => b.lastPrayedAt - a.lastPrayedAt)
    .slice(0, MAX_INTENTIONS);
}

function mergeBy(itemsHere, itemsImported, key) {
  const seen = new Set(itemsHere.map((item) => item[key]));
  return [...itemsHere, ...itemsImported.filter((item) => !seen.has(item[key]))];
}

/** Build a serialisable private-backup payload from device-local storage. */
export function createBackupPayload(now = new Date()) {
  const textSize = readString(TEXT_SIZE_KEY);
  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    createdAt: now.toISOString(),
    data: {
      savedPrayers: cleanPrayers(readJson(SAVED_PRAYERS_KEY, [])),
      savedVerses: cleanVerses(readJson(SAVED_VERSES_KEY, [])),
      intentions: cleanIntentions(readJson(INTENTIONS_KEY, [])),
      textSize: TEXT_SIZES.has(textSize) ? textSize : 'comfortable',
      reminderTimes: getReminderTimes(),
    },
  };
}

export function serializeBackup(now = new Date()) {
  return JSON.stringify(createBackupPayload(now), null, 2);
}

/**
 * Remove the device-local content that may contain private prayer information.
 * Account, purchase, allowance, reminder, and reading-size data are deliberately
 * left alone. The caller must obtain explicit confirmation before invoking this.
 */
export function clearPrivateContent() {
  try {
    window.localStorage.removeItem(SAVED_PRAYERS_KEY);
    window.localStorage.removeItem(SAVED_VERSES_KEY);
    window.localStorage.removeItem(INTENTIONS_KEY);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Merge a backup string into local storage and return the resulting app data.
 * @throws {Error} when the file is not a recognised Beloved backup.
 */
export function restoreBackup(raw) {
  let payload;
  try {
    payload = JSON.parse(raw);
  } catch (e) {
    throw new Error('That file is not a valid Beloved backup.');
  }
  if (
    !payload ||
    payload.format !== BACKUP_FORMAT ||
    payload.version !== BACKUP_VERSION ||
    !payload.data ||
    typeof payload.data !== 'object'
  ) {
    throw new Error('That file is not a recognised Beloved backup.');
  }

  const currentPrayers = cleanPrayers(readJson(SAVED_PRAYERS_KEY, []));
  const currentVerses = cleanVerses(readJson(SAVED_VERSES_KEY, []));
  const currentIntentions = cleanIntentions(readJson(INTENTIONS_KEY, []));
  const importedPrayers = cleanPrayers(payload.data.savedPrayers);
  const importedVerses = cleanVerses(payload.data.savedVerses);
  const importedIntentions = cleanIntentions(payload.data.intentions);

  const savedPrayers = mergeBy(currentPrayers, importedPrayers, 'id').slice(0, 250);
  const savedVerses = mergeBy(currentVerses, importedVerses, 'id').slice(0, 250);
  const intentions = mergeBy(currentIntentions, importedIntentions, 'theme')
    .sort((a, b) => b.lastPrayedAt - a.lastPrayedAt)
    .slice(0, MAX_INTENTIONS);
  const textSize = TEXT_SIZES.has(payload.data.textSize)
    ? payload.data.textSize
    : readString(TEXT_SIZE_KEY) || 'comfortable';
  const reminderTimes = normaliseReminderTimes(payload.data.reminderTimes);

  writeJson(SAVED_PRAYERS_KEY, savedPrayers);
  writeJson(SAVED_VERSES_KEY, savedVerses);
  writeJson(INTENTIONS_KEY, intentions);
  window.localStorage.setItem(TEXT_SIZE_KEY, textSize);
  writeJson(REMINDER_TIMES_KEY, reminderTimes);

  return { savedPrayers, savedVerses, intentions, textSize, reminderTimes };
}

/**
 * Share the backup file on supported phones, or download it on the web.
 * This must be called from an explicit user action.
 */
export async function exportBackupFile() {
  const contents = serializeBackup();
  const date = new Date().toISOString().slice(0, 10);
  const filename = `beloved-private-backup-${date}.json`;
  const blob = new Blob([contents], { type: 'application/json' });

  if (typeof File !== 'undefined' && typeof navigator.share === 'function') {
    const file = new File([blob], filename, { type: 'application/json' });
    const canShare = typeof navigator.canShare !== 'function' || navigator.canShare({ files: [file] });
    if (canShare) {
      await navigator.share({
        files: [file],
        title: 'Beloved private backup',
        text: 'A private backup of saved Beloved content.',
      });
      return 'shared';
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return 'downloaded';
}

/** Read and restore a backup selected through a file input. */
export async function restoreBackupFile(file) {
  if (!file) {
    throw new Error('Please choose a Beloved backup file.');
  }
  if (Number.isFinite(file.size) && file.size > 2_000_000) {
    throw new Error('That backup is too large to be a Beloved backup.');
  }
  if (typeof file.text === 'function') {
    return restoreBackup(await file.text());
  }
  const raw = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('That backup file could not be read.'));
    reader.readAsText(file);
  });
  return restoreBackup(raw);
}
